import { addDays, addMonths, DateOnly, format } from '@if/core/date'
import { t } from '@if/core/i18n'
import {
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentMethod,
  EPaymentPlan,
  EWorksheetStatus,
  getEPaymentMethodList,
} from '@if/core/types'
import { formatDate, normalizeDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { FinancingConfigDto, WarrantyPriceTierDto } from '../data/api/configs-api/financing-config-api'
import { FinancingProgramConfig, FinancingProgramConfigs } from './Constants'

/**
 * Resolves the maximum allowed extended-warranty amount for a given vehicle selling price by walking the
 * configured tiers. Returns the smallest bounded tier whose `maxVehiclePrice` covers the price; if the price
 * exceeds every bounded tier, falls back to the unbounded catch-all tier (`maxVehiclePrice == null`).
 */
export const getWarrantyMaxForVehiclePrice = (
  tiers: WarrantyPriceTierDto[] | undefined,
  vehicleSellingAmount: number | null | undefined,
): number | null => {
  if (!tiers?.length || vehicleSellingAmount == null) return null
  const coveringTier = tiers
    .filter((t) => t.maxVehiclePrice != null && vehicleSellingAmount <= t.maxVehiclePrice)
    .sort((a, b) => a.maxVehiclePrice! - b.maxVehiclePrice!)[0]
  if (coveringTier) return coveringTier.maxWarrantyAmount
  const catchAllTier = tiers.find((t) => t.maxVehiclePrice == null)
  return catchAllTier ? catchAllTier.maxWarrantyAmount : null
}

/**
 * Product-cap validation options. When the feature is on, the merchant worksheet must respect the warranty
 * tier cap (Rule A) and the products total cap (applied directly to amountRequested), both sourced from the
 * per-merchant financing config.
 */
export interface ProductsCapOptions {
  financingConfig?: FinancingConfigDto
  featureEnabled?: boolean
}

/**
 * The longest term a requested amount may take, over three tiers: `longTerm` unlocks at
 * `largeLoanAmount`, `maxTerm` at `baseAmountThreshold`, and `baseAmountMaxTerm` caps everything
 * below it. Mirrors the backend rule the buy-down term & plan endpoint applies
 * (`TermPlanOptionsService.AmountBasedMaxTerm`); `CalculateMonthlyTerms` does not yet know the
 * bottom tier, so under `baseAmountThreshold` this is the only thing keeping 72 off the list.
 */
export const getMaxTermForAmount = (
  config: Pick<
    FinancingProgramConfig,
    'maxTerm' | 'longTerm' | 'largeLoanAmount' | 'baseAmountThreshold' | 'baseAmountMaxTerm'
  >,
  amountRequested: number | null | undefined,
): number => {
  const amount = amountRequested ?? 0
  if (config.largeLoanAmount > 0 && amount >= config.largeLoanAmount) return config.longTerm
  if (config.baseAmountMaxTerm > 0 && amount < config.baseAmountThreshold)
    return Math.min(config.baseAmountMaxTerm, config.maxTerm)
  return config.maxTerm
}

export const buildWorksheetBaseSchema = (config: FinancingProgramConfig) =>
  yup.object({
    amountRequested: yup
      .double()
      .required()
      .positive()
      .min(config.minLoanAmount, () => t('common.errors.greaterOrEqual_other', { min: config.minLoanAmount }))
      .max(config.maxLoanAmount, () => t('common.errors.lowerOrEqual_other', { max: config.maxLoanAmount })),

    deliveryOn: yup.text().default(new Date().toDateString()).required(),

    paymentFrequency: yup.mixed<EPaymentFrequency>().default(EPaymentFrequency.Monthly).required(),
    paymentPlanId: yup.mixed<EPaymentPlan>().required().default(EPaymentPlan.RegularDailyInterests),

    firstPaymentOn: yup
      .dateOnly()
      .nullable()
      .test('deliveryOn-test', function (value?: DateOnly | null) {
        const deliveryOn = (this.parent as { deliveryOn: string | Date | null }).deliveryOn
        if (!deliveryOn) return true
        const twoMonthsLater = new Date(deliveryOn)
        twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2)

        return !value || (new Date(value) > new Date(deliveryOn) && new Date(value) <= twoMonthsLater)
      }),

    term: yup
      .integer()
      .when('paymentPlanId', ([paymentPlanId], schema) =>
        paymentPlanId === EPaymentPlan.RegularDailyInterests ? schema.min(config.minTerm) : schema.min(1),
      )
      .test('max-term', function (value) {
        const parent = this.parent as { amountRequested?: number | null } | undefined
        const max = getMaxTermForAmount(config, parent?.amountRequested)
        if (value == null || value <= max) return true
        return this.createError({ message: t('worksheet.errors.maxTerm', { max }) })
      })
      .required(),

    creditApplicationId: yup.text(),

    id: yup.text(),

    versionTag: yup.text().default(''),

    isHighEstimate: yup.bool(),
  })

export type WorsheetDetailsDto = yup.InferSubmit<ReturnType<typeof buildWorksheetBaseSchema>>

export const buildEditPersonalLoanWorksheetDtoSchema = (
  hardHitReportReceivedOn: Date,
  finalDecisionMaxAmountFinanced: number,
  merchantPaymentPlanId: EPaymentPlan,
  configOverride?: Partial<FinancingProgramConfig>,
  validTermAndPlanKeys?: Set<string>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.Personal], ...configOverride }
  const effectiveMaxLoanAmount = Math.min(config.maxLoanAmount, finalDecisionMaxAmountFinanced)
  const defaultSchema = buildWorksheetBaseSchema({ ...config, maxLoanAmount: effectiveMaxLoanAmount })
  return defaultSchema.shape({
    term: (defaultSchema.fields.term as yup.NumberSchema)
      .test(
        'valid-term-and-plan-test',
        () => t('worksheet.errors.invalidTermAndPlan'),
        function (value) {
          if (value == null) return true
          if (!validTermAndPlanKeys?.size) return true
          const paymentPlanId = (this.parent as { paymentPlanId: string }).paymentPlanId
          return validTermAndPlanKeys.has(`${paymentPlanId}|${value}`)
        },
      )
      .nonNullable()
      .required(),

    status: yup.text().required().default(EWorksheetStatus.Draft),

    // The merchant-payment-breakdown total is checked at submit time only (see `saveWorksheet` /
    // `sumMerchantPayments`), not here: field-level validation runs on every blur of this field,
    // which fired a false-positive mismatch the moment the user left it, before they'd even reached
    // the (still-empty) payment breakdown below (#69195).
    amountRequested: (defaultSchema.fields.amountRequested as yup.NumberSchema)
      .required()
      .max(effectiveMaxLoanAmount, () => t('common.errors.lowerOrEqual_finalDecisionMaxAmountFinanced')),

    deliveryOn: yup
      .dateOnly()
      .default(() => new DateOnly(new Date()))
      // The API serves DeliveryOn as a nullable DateOnly, and a saved worksheet can
      // carry null — casting the loaded record through this schema threw before this.
      // Still required(), so validation blocks submit until a date is picked; the
      // Products / MechanicalWarranty schemas below already declare it this way.
      .nullable()
      .required()
      .test(
        'expiresOn-test',
        () => t('common.errors.deliveryOnLowerThanExpiresOn'),
        function (value) {
          const deliveryDate = new Date(value as Date)
          const expiredDate = new Date(hardHitReportReceivedOn)
          expiredDate.setDate(expiredDate.getDate() + 90)
          return deliveryDate <= expiredDate
        },
      )
      .test(
        'deliveryOn-test',
        () => t('common.errors.deliveryOnGreaterOrEqualThanToday'),
        function (value) {
          const deliveryDate = new Date(value as Date)
          deliveryDate.setUTCHours(0, 0, 0, 0)

          const now = new Date()
          now.setUTCHours(0, 0, 0, 0)

          return now <= deliveryDate
        },
      ),
    includeInsurance: yup.bool(),

    addFastPayments: yup.bool(),

    fundConfirmationNote: yup.text(),

    isHighEstimate: yup.bool(),

    highEstimateExplainedToClient: yup.bool(),

    merchantPayments: yup.mixed<MerchantPayment[]>().default([]),

    paymentPlanId: yup.mixed<EPaymentPlan>().required().default(merchantPaymentPlanId),
  })
}

// ignoreProvider: when the provider is forced by the system (ex: SymTech prime), it must not
// mark the insurance group as "started" and make the other fields required by itself
const anyInsuranceFieldProvided = (values: ProductsInsurance, ignoreProvider: boolean) =>
  !!values.amount || !!values.term || !!values.police || (!ignoreProvider && !!values.provider)

const testInsuranceSchemaField = (textContext: yup.TestContext<yup.AnyObject>, ignoreProvider = false) => {
  const values = textContext.parent as ProductsInsurance

  const anyFieldProvided = anyInsuranceFieldProvided(values, ignoreProvider)

  const field = textContext.path.split('.')[1] as keyof ProductsInsurance
  const value = values[field]

  if (anyFieldProvided && !value)
    return textContext.resolve(
      textContext.createError({ path: textContext.path, message: t('mixed.required', { ns: 'validation' }) }),
    )

  return true
}

const testEndDateRequired = (textContext: yup.TestContext<yup.AnyObject>, ignoreProvider = false) => {
  const values = textContext.parent as ProductsInsurance

  const anyOtherFieldProvided = anyInsuranceFieldProvided(values, ignoreProvider)
  const endDateValue = values.endDate

  if (anyOtherFieldProvided && !endDateValue) {
    return textContext.resolve(
      textContext.createError({
        path: textContext.path,
        message: t('mixed.required', { ns: 'validation' }),
      }),
    )
  }

  return true
}
const withEndDateRequired = (schema: yup.ObjectSchema<ProductsInsurance>, ignoreProvider = false) => {
  const endDate = schema.fields.endDate as yup.DateOnlySchema<DateOnly | null>
  schema.fields.endDate = endDate.test(function () {
    return testEndDateRequired(this, ignoreProvider)
  })
  return schema
}

export const buildProductInsuranceSchema = (config: FinancingProgramConfig, ignoreProvider = false) =>
  yup.object({
    provider: yup
      .text()
      .nullable()
      .default(null)
      .test(function () {
        return testInsuranceSchemaField(this, ignoreProvider)
      }),
    police: yup
      .text()
      .nullable()
      .default(null)
      .test(function () {
        return testInsuranceSchemaField(this, ignoreProvider)
      }),
    term: yup
      .integer()
      .min(config.minTerm)
      .max(config.maxTerm)
      .test(function () {
        return testInsuranceSchemaField(this, ignoreProvider)
      }),
    amount: yup
      .double()
      .positive()
      .test(function () {
        return testInsuranceSchemaField(this, ignoreProvider)
      }),
    endDate: yup
      .dateOnly()
      .nullable()
      .default(null)
      .when('term', ([term], schema) => {
        if (term && !isNaN(Number(term))) {
          const now = new Date()
          const date = DateOnly.fromComponents(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
          const minDate = new DateOnly(addMonths(date, Number(term)))
          const maxDate = new DateOnly(addDays(addMonths(date, Number(term)), 21))

          return schema.default(minDate).test('min-date', (val, ctx) => {
            const isExtendedWarranty = ctx.path.includes('extendedWarranty')

            if (isExtendedWarranty) {
              return true
            }

            if (!val) return true
            if (val < minDate) {
              return ctx.createError({
                message: t('common.errors.invalidMinDate', { minValue: format(minDate, 'yyyy-MM-dd') }),
              })
            }
            if (val > maxDate) {
              return ctx.createError({
                message: t('common.errors.invalidMaxDate', { maxValue: format(maxDate, 'yyyy-MM-dd') }),
              })
            }
            return true
          })
        }
        return schema
      }),
  })

export type ProductsInsurance = yup.InferSubmit<ReturnType<typeof buildProductInsuranceSchema>>

export const buildMechanicalWarrantyAdditionsSchema = () =>
  yup.object({
    options: yup.array().of(yup.text().required()).required().default([]),
    surcharges: yup.array().of(yup.text().required()).required().default([]),
  })

const resolveInsuranceMaxTerm = (schema: yup.ObjectSchema<ProductsInsurance>, vehicleFinancingTerm: number) => {
  if (vehicleFinancingTerm)
    schema.fields.term = (
      schema.fields.term as yup.NumberSchema<number | null | undefined, yup.AnyObject, null, 'd'>
    ).max(vehicleFinancingTerm)

  return schema
}

const resolveInsuranceMinTerm = (schema: yup.ObjectSchema<ProductsInsurance>, loanTerm: number) => {
  if (loanTerm)
    schema.fields.term = (
      schema.fields.term as yup.NumberSchema<number | null | undefined, yup.AnyObject, null, 'd'>
    ).min(loanTerm)

  return schema
}

export const getFirstPaymentOnMinMax = (
  deliveryOn: string | Date,
  paymentFrequency: EPaymentFrequency,
): [Date, Date] => {
  const deliveryDate = normalizeDateTime(deliveryOn)
  switch (paymentFrequency) {
    case EPaymentFrequency.Monthly:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 30)]
    case EPaymentFrequency.BiWeekly:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 14)]
    case EPaymentFrequency.Single:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 7)]
    default:
      return [addDays(deliveryDate, 2), addDays(deliveryDate, 5)]
  }
}

const validateProductsAndMechWarrantyDeliveryOnRange = (
  val: DateOnly | null | undefined,
  ctx: yup.TestContext<yup.AnyObject>,
): boolean | yup.ValidationError => {
  const deliveryOn = (ctx.parent as ProductsWorksheet | MechanicalWarrantyWorksheet).deliveryOn
  const paymentFrequency = (ctx.parent as ProductsWorksheet | MechanicalWarrantyWorksheet).paymentFrequency
  if (!val || !deliveryOn || !paymentFrequency) return true

  const [minDate, maxDate] = getFirstPaymentOnMinMax(deliveryOn, paymentFrequency)
  const normalizedVal = normalizeDateTime(val)

  if (normalizedVal < minDate) {
    return ctx.createError({ message: t('validation:date.min', { min: formatDate(minDate) }) })
  }

  if (normalizedVal > maxDate) {
    return ctx.createError({ message: t('validation:date.max', { max: formatDate(maxDate) }) })
  }

  return true
}

export const buildProductWorksheetSchema = (
  capOptions: ProductsCapOptions = {},
  isSymTechPrime: boolean,
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.Products], ...configOverride }
  const baseSchema = buildWorksheetBaseSchema(config)
  const baseInsuranceSchema = buildProductInsuranceSchema(config)

  const maxProductsTotal = capOptions.featureEnabled
    ? (capOptions.financingConfig?.maxProductsTotalAmount ?? config.maxLoanAmount)
    : config.maxLoanAmount

  const extendedWarranty = baseInsuranceSchema

  const replacementOrGapInsurance = withEndDateRequired(baseInsuranceSchema.clone()).when('term', ([term], schema) =>
    resolveInsuranceMinTerm(schema, term as number),
  )

  // SymTech prime: the forced provider must not make the other fields required
  const creditInsurance = withEndDateRequired(buildProductInsuranceSchema(config, isSymTechPrime), isSymTechPrime).when(
    'term',
    ([term], schema) => resolveInsuranceMinTerm(schema, term as number),
  )

  return (
    baseSchema
      .shape({
        deliveryOn: yup
          .dateOnly()
          .default(() => new DateOnly(new Date()))
          .nullable()
          .required(),
        firstPaymentOn: yup
          .dateOnly()
          .required()
          .test('deliveryOn-range', (val, ctx) => validateProductsAndMechWarrantyDeliveryOnRange(val, ctx))
          .default(() => new DateOnly(addDays<Date>(new Date(), 2))),
        amountRequested: (baseSchema.fields.amountRequested as yup.NumberSchema).max(maxProductsTotal, () =>
          t('productsWorksheet.errors.productsTotalExceedsCap', { max: maxProductsTotal }),
        ),
        term: (baseSchema.fields.term as yup.NumberSchema).nullable().required(),
        vehicleFinancingTerm: yup.integer().required().min(config.minTerm).max(240),
        vehicleSellingAmount: capOptions.featureEnabled ? yup.double().required().positive() : yup.double(),
        extendedWarranty: extendedWarranty
          .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
            return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
          })
          .nullable(),
        replacementOrGapInsurance: replacementOrGapInsurance
          .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
            return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
          })
          .nullable(),
        creditInsurance: creditInsurance
          .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
            return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
          })
          .nullable(),
      })
      // Rule A — extended warranty capped by the vehicle-price tier.
      .test('warranty-tier-cap', (value, ctx) => {
        if (!capOptions.featureEnabled) return true
        const worksheet = value
        const warrantyAmount = worksheet.extendedWarranty?.amount
        const maxWarranty = getWarrantyMaxForVehiclePrice(
          capOptions.financingConfig?.warrantyMaxByVehiclePriceTier,
          worksheet.vehicleSellingAmount,
        )
        if (warrantyAmount == null || maxWarranty == null) return true
        if (warrantyAmount > maxWarranty) {
          return ctx.createError({
            path: 'extendedWarranty.amount',
            message: t('productsWorksheet.errors.warrantyExceedsTierCap', { max: maxWarranty }),
          })
        }
        return true
      })
  )
}

export type ProductsWorksheet = yup.InferSubmit<ReturnType<typeof buildProductWorksheetSchema>>

export const EditPersonalLoanWorksheetDtoSchema = buildEditPersonalLoanWorksheetDtoSchema(
  new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // Adds 90 days in milliseconds to the current date
  FinancingProgramConfigs[EFinancingProgram.Personal].maxLoanAmount,
  EPaymentPlan.RegularDailyInterests,
)

export type EditPersonalLoanWorksheetDto = yup.InferSubmit<typeof EditPersonalLoanWorksheetDtoSchema>

export type EditPersonalLoanWorksheetDtoDefaultValue = yup.InferSubmit<typeof EditPersonalLoanWorksheetDtoSchema>

export const merchantPaymentSchema = yup.object({
  merchantId: yup.text().required(),
  amount: yup.double().positive().required(),
  paymentMethod: yup.text().default(EPaymentMethod.BankTransfer).required().oneOf(getEPaymentMethodList()),
  merchantName: yup.text().required(),
})

export type MerchantPayment = yup.InferSubmit<typeof merchantPaymentSchema>

export const sumMerchantPayments = (merchantPayments: MerchantPayment[]): number =>
  merchantPayments.reduce((acc, curr) => Number(acc) + Number(curr.amount), 0)

export const buildComputePossibleTermsDtoSchema = (
  maxAmountFinanced: number,
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.Personal], ...configOverride }

  return yup.object({
    merchantId: yup.text().required(),
    paymentPlanId: yup.text().required(),
    interestRate: yup.double().positive().required(),
    hasCoapplicant: yup.bool(),
    stateIso: yup.text().required(),
    paymentFrequency: yup.text().oneOf([EPaymentFrequency.Monthly]).required(),
    deliveryOn: yup.text().default(new Date().toDateString()).required(),
    firstPaymentOn: yup.date().test('deliveryOn-test', function (value?: Date) {
      const deliveryOn = (this.parent as { deliveryOn: string }).deliveryOn
      const twoMonthsLater = new Date(deliveryOn)
      twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2)

      return !deliveryOn || !value || (value > new Date(deliveryOn) && value <= twoMonthsLater)
    }),
    maxPmtAmount: yup.double().positive().required(),
    amountRequested: yup
      .double()
      .required('AR')
      .positive()
      .min(config.minLoanAmount, () => t('common.errors.greaterOrEqual_MinimumLoanAmount'))
      .max(maxAmountFinanced, () => t('common.errors.lowerOrEqual_MaximumLoanAmount')),
    includeInsurance: yup.bool(),
  })
}

export const buildMechanicalWarrantyWorksheetSchema = (
  creditApplication?: { isSinglePayment?: boolean },
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.MechanicalWarranty], ...configOverride }
  const baseSchema = buildWorksheetBaseSchema(config)
  const baseAdditionsSchema = buildMechanicalWarrantyAdditionsSchema()
  return baseSchema.shape({
    deliveryOn: yup
      .dateOnly()
      .default(() => new DateOnly(new Date()))
      .nullable()
      .required(),
    firstPaymentOn: yup
      .dateOnly()
      .required()
      .test('deliveryOn-range', (val, ctx) => validateProductsAndMechWarrantyDeliveryOnRange(val, ctx))
      .default(() => new DateOnly(addDays<Date>(new Date(), 2))),
    term: yup.integer().min(config.minTerm).max(config.maxTerm).required().default(config.minTerm),
    providerId: yup.text().required(),
    policy: yup.text().required().default(null),
    additions: baseAdditionsSchema.default(baseAdditionsSchema.getDefault()),
    mechanicalWarrantyProductId: yup.text().required().default(null),
    paymentFrequency: yup
      .mixed<EPaymentFrequency>()
      .default(creditApplication?.isSinglePayment ? EPaymentFrequency.Single : EPaymentFrequency.Monthly)
      .required()
      .test('valid-payment-frequency', function (value) {
        if (creditApplication?.isSinglePayment) {
          return (
            value === EPaymentFrequency.Single ||
            this.createError({
              message: t('common.errors.singlePaymentOnly'),
            })
          )
        } else {
          return (
            value === EPaymentFrequency.Monthly ||
            value === EPaymentFrequency.BiWeekly ||
            this.createError({
              message: t('common.errors.monthlyOrBiWeeklyOnly'),
            })
          )
        }
      }),
  })
}

export type MechanicalWarrantyWorksheet = yup.InferSubmit<ReturnType<typeof buildMechanicalWarrantyWorksheetSchema>>

export const buildEditIfXpressWorksheetDtoSchema = (
  hardHitReportReceivedOn: Date,
  finalDecisionMaxAmountFinanced: number,
  merchantPaymentPlanId: EPaymentPlan,
  configOverride?: Partial<FinancingProgramConfig>,
) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.IfXpress], ...configOverride }
  const effectiveMaxLoanAmount = Math.min(config.maxLoanAmount, finalDecisionMaxAmountFinanced)
  const defaultSchema = buildWorksheetBaseSchema({ ...config, maxLoanAmount: effectiveMaxLoanAmount })
  return defaultSchema.shape({
    status: yup.text().required().default(EWorksheetStatus.Draft),

    amountRequested: (defaultSchema.fields.amountRequested as yup.NumberSchema)
      .required()
      .max(effectiveMaxLoanAmount, () => t('common.errors.lowerOrEqual_finalDecisionMaxAmountFinanced')),

    term: (defaultSchema.fields.term as yup.NumberSchema).required(),

    deliveryOn: yup
      .dateOnly()
      .default(() => new DateOnly(new Date()))
      // The API serves DeliveryOn as a nullable DateOnly, and a saved worksheet can
      // carry null — casting the loaded record through this schema threw before this.
      // Still required(), so validation blocks submit until a date is picked; the
      // Products / MechanicalWarranty schemas below already declare it this way.
      .nullable()
      .required()
      .test(
        'expiresOn-test',
        () => t('common.errors.deliveryOnLowerThanExpiresOn'),
        function (value) {
          const deliveryDate = new Date(value as Date)
          const expiredDate = new Date(hardHitReportReceivedOn)
          expiredDate.setDate(expiredDate.getDate() + 90)
          return deliveryDate <= expiredDate
        },
      )
      .test(
        'deliveryOn-test',
        () => t('common.errors.deliveryOnGreaterOrEqualThanToday'),
        function (value) {
          const deliveryDate = new Date(value as Date)
          deliveryDate.setUTCHours(0, 0, 0, 0)

          const now = new Date()
          now.setUTCHours(0, 0, 0, 0)

          return now <= deliveryDate
        },
      ),
    includeInsurance: yup.bool(),

    addFastPayments: yup.bool(),

    fundConfirmationNote: yup.text(),

    highEstimateExplainedToClient: yup.bool(),

    paymentPlanId: yup.mixed<EPaymentPlan>().required().default(merchantPaymentPlanId),
  })
}

export const EditIfXpressWorksheetDtoSchema = buildEditIfXpressWorksheetDtoSchema(
  new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // Adds 90 days in milliseconds to the current date
  FinancingProgramConfigs[EFinancingProgram.IfXpress].maxLoanAmount,
  EPaymentPlan.RegularDailyInterests,
)

export type EditIfXpressWorksheetDto = yup.InferSubmit<typeof EditIfXpressWorksheetDtoSchema>

export type EditIfXpressWorksheetDtoDefaultValue = yup.InferSubmit<typeof EditIfXpressWorksheetDtoSchema>

export const buildComputePossibleTermsIfXpressDtoSchema = (configOverride?: Partial<FinancingProgramConfig>) => {
  const config = { ...FinancingProgramConfigs[EFinancingProgram.IfXpress], ...configOverride }

  return yup.object({
    merchantId: yup.text().required(),
    paymentPlanId: yup.text().required(),
    interestRate: yup.double().positive().required(),
    hasCoapplicant: yup.bool(),
    stateIso: yup.text().required(),
    paymentFrequency: yup.text().oneOf(Object.values(EPaymentFrequency)).required(),
    deliveryOn: yup.text().default(new Date().toDateString()).required(),
    firstPaymentOn: yup.date().test('deliveryOn-test', function (value?: Date) {
      const deliveryOn = (this.parent as { deliveryOn: string }).deliveryOn
      const twoMonthsLater = new Date(deliveryOn)
      twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2)

      return !deliveryOn || !value || (value > new Date(deliveryOn) && value <= twoMonthsLater)
    }),
    maxPmtAmount: yup.double().positive().required(),
    amountRequested: yup
      .double()
      .required('AR')
      .positive()
      .min(config.minLoanAmount, () => t('common.errors.greaterOrEqual_MinimumLoanAmount'))
      .max(config.maxLoanAmount, () => t('common.errors.lowerOrEqual_MaximumLoanAmount')),
    includeInsurance: yup.bool(),
  })
}

export const computePossibleTermsIfXpressDtoSchema = buildComputePossibleTermsIfXpressDtoSchema()

export type ComputePossibleTermsIfXpressDto = yup.InferSubmit<typeof computePossibleTermsIfXpressDtoSchema>
